/**
 * iter314 — Suppression groupée de clients.
 * Les fiches sans comptabilité sont supprimées ; celles qui portent des OR, devis ou
 * factures sont archivées pour ne jamais laisser d'écriture orpheline. On montre le
 * tri avant d'agir, et le compte rendu après.
 */
import { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Trash2, Archive, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function ClientsSuppressionDialog({ open, onClose, ids = [], onDone }) {
  const [items, setItems] = useState([]);
  const [chargement, setChargement] = useState(false);
  const [busy, setBusy] = useState(false);

  const charger = useCallback(async () => {
    if (!open || ids.length === 0) return;
    setChargement(true);
    try {
      const { data } = await api.post("/clients/selection/apercu", { ids });
      setItems((data.items || []).filter((i) => !i.introuvable));
    } catch {
      toast.error("Impossible de charger les fiches sélectionnées");
    } finally {
      setChargement(false);
    }
  }, [open, ids]);

  useEffect(() => { charger(); }, [charger]);

  const aSupprimer = items.filter((i) => !i.a_de_la_compta);
  const aArchiver = items.filter((i) => i.a_de_la_compta && !i.archive);
  const dejaArchives = items.filter((i) => i.a_de_la_compta && i.archive);

  const executer = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/clients/selection/supprimer", { ids });
      const r = data.resume || {};
      const details = [
        r.supprimes ? `${r.supprimes} supprimée(s)` : null,
        r.archives ? `${r.archives} archivée(s)` : null,
        r.deja_archives ? `${r.deja_archives} déjà archivée(s)` : null,
      ].filter(Boolean).join(" · ");
      toast.success("Sélection traitée", { description: details || "Aucun changement" });
      onDone?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
    } finally {
      setBusy(false);
    }
  };

  const Bloc = ({ titre, Icon, couleur, liste, note, testid }) => (
    liste.length > 0 && (
      <div className={`rounded-lg border p-3 ${couleur}`} data-testid={testid}>
        <div className="flex items-center gap-2 font-semibold text-sm">
          <Icon className="w-4 h-4" /> {titre} ({liste.length})
        </div>
        <p className="text-xs mt-1 opacity-80">{note}</p>
        <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto">
          {liste.map((i) => (
            <li key={i.id} className="text-xs flex justify-between gap-2">
              <span className="truncate">{i.label}</span>
              <span className="shrink-0 opacity-70">
                {i.dependances.or || 0} OR · {i.dependances.factures || 0} fact. · {i.dependances.vehicules || 0} véh.
              </span>
            </li>
          ))}
        </ul>
      </div>
    )
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose?.()}>
      <DialogContent className="sm:max-w-lg" data-testid="clients-suppression-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-red-600" /> Supprimer {items.length} client{items.length > 1 ? "s" : ""}
          </DialogTitle>
          <DialogDescription>
            Une fiche liée à des OR, devis ou factures ne peut pas être supprimée sans casser la
            comptabilité : elle sera archivée à la place.
          </DialogDescription>
        </DialogHeader>

        {chargement ? (
          <div className="py-10 text-center text-gray-400 text-sm">Analyse des fiches…</div>
        ) : (
          <div className="space-y-3">
            <Bloc
              titre="Supprimées définitivement"
              Icon={Trash2}
              couleur="bg-red-50 border-red-200 text-red-800"
              liste={aSupprimer}
              note="Aucun historique comptable : la fiche et ses véhicules disparaissent."
              testid="suppression-bloc-supprimees"
            />
            <Bloc
              titre="Archivées à la place"
              Icon={Archive}
              couleur="bg-amber-50 border-amber-200 text-amber-800"
              liste={aArchiver}
              note="Retirées de la liste active, historique intact et réversible."
              testid="suppression-bloc-archivees"
            />
            <Bloc
              titre="Déjà archivées"
              Icon={Archive}
              couleur="bg-gray-50 border-gray-200 text-gray-700"
              liste={dejaArchives}
              note="Rien à faire, elles restent telles quelles."
              testid="suppression-bloc-inchangees"
            />

            {aSupprimer.length > 0 && (
              <div className="flex gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-800">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>Cette suppression est définitive et sans corbeille.</span>
              </div>
            )}

            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={onClose} disabled={busy} data-testid="suppression-annuler">
                Annuler
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={executer}
                disabled={busy || items.length === 0}
                data-testid="suppression-confirmer"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Confirmer
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
